'use client';

// Inscription (cahier §4.1). Ne connecte pas : après création, on envoie vers la
// vérification e-mail (code 6 chiffres).

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { PasswordPolicyHint } from '@/components/auth/PasswordPolicyHint';
import { SocialAuthButtons } from '@/components/auth/SocialAuthButtons';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Text } from '@/components/ui/Text';
import { useT } from '@/hooks/useT';
import { authErrorKey } from '@/lib/authError';
import { useRegister } from '@/queries/auth';
import { useAuthFlow } from '@/stores/authFlow';
import { ApiError } from '@/types/api';

interface FormValues {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  password_confirmation: string;
}

export default function SignUpPage() {
  const { t, i18n } = useT('auth');
  const router = useRouter();
  const registerMutation = useRegister();
  const setPendingEmail = useAuthFlow((s) => s.setPendingEmail);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
    setError,
    formState: { errors },
  } = useForm<FormValues>();
  const password = useWatch({ control, name: 'password', defaultValue: '' });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    if (values.password !== values.password_confirmation) {
      setError('password_confirmation', { message: t('validation.passwordMismatch') });
      return;
    }
    try {
      await registerMutation.mutateAsync({ ...values, locale: i18n.language });
      setPendingEmail(values.email);
      router.push('/verify-code');
    } catch (e) {
      // Erreurs de validation backend (e-mail déjà pris, mot de passe compromis…).
      if (e instanceof ApiError && e.code === 'VALIDATION_ERROR') {
        Object.entries(e.errors).forEach(([field, msgs]) => {
          setError(field as keyof FormValues, { message: msgs[0] });
        });
        return;
      }
      setFormError(t(`errors.${authErrorKey(e)}`));
    }
  });

  return (
    <div className="flex flex-col gap-8">
      <header className="flex flex-col gap-2">
        <Text as="h1" variant="displayXl" className="font-display">
          {t('signUp.title')}
        </Text>
        <Text variant="bodyLg" className="text-text-secondary">
          {t('signUp.subtitle')}
        </Text>
      </header>

      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <div className="flex gap-3">
          <div className="flex-1">
            <Input
              label={t('signUp.firstName')}
              autoComplete="given-name"
              error={errors.first_name?.message ?? (errors.first_name ? t('validation.firstNameRequired') : undefined)}
              {...register('first_name', { required: true })}
            />
          </div>
          <div className="flex-1">
            <Input
              label={t('signUp.lastName')}
              autoComplete="family-name"
              error={errors.last_name?.message ?? (errors.last_name ? t('validation.lastNameRequired') : undefined)}
              {...register('last_name', { required: true })}
            />
          </div>
        </div>

        <Input
          label={t('signUp.email')}
          type="email"
          autoComplete="email"
          placeholder={t('signIn.emailPlaceholder')}
          error={errors.email?.message ?? (errors.email ? t('validation.emailInvalid') : undefined)}
          {...register('email', { required: true, pattern: /.+@.+\..+/ })}
        />

        <Input
          label={t('signUp.password')}
          type="password"
          autoComplete="new-password"
          error={errors.password?.message}
          {...register('password', { required: true })}
        />
        <PasswordPolicyHint password={password} />

        <Input
          label={t('signUp.confirmPassword')}
          type="password"
          autoComplete="new-password"
          error={errors.password_confirmation?.message}
          {...register('password_confirmation', { required: true })}
        />

        {formError ? (
          <Text variant="bodySm" className="text-danger" role="alert">
            {formError}
          </Text>
        ) : null}

        <Button type="submit" fullWidth loading={registerMutation.isPending}>
          {t('signUp.submit')}
        </Button>

        {/* La phrase sous le bouton (2026-10-28) ; l'acceptation elle-même est demandée juste après. */}
        <Text variant="bodySm" className="text-center text-text-secondary">
          {t('signUp.termsLead')}
          <a href={lienDuTexte('cgu')} target="_blank" rel="noreferrer" className="text-action hover:underline">
            {t('signUp.termsLink')}
          </a>
          {t('signUp.termsEnd')}
        </Text>
      </form>

      <SocialAuthButtons />

      <Text variant="bodySm" className="text-center text-text-tertiary">
        {t('signUp.haveAccount')}{' '}
        <Link href="/sign-in" className="text-action hover:underline">
          {t('signUp.signInLink')}
        </Link>
      </Text>
    </div>
  );
}
